import { useCallback, useRef, useState, type DragEvent, type JSX } from 'react';
import styles from './ImageUploadZone.module.css';
import type { ImageUploadZoneProps } from './ImageUploadZone.types';

const ImageUploadZone = ({
  onImagesSelected,
  uploadedImages,
  onRemoveImage,
  maxImages = 70,
  disabled = false,
}: ImageUploadZoneProps): JSX.Element => {
  const [isDragActive, setIsDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDragEnter = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      if (!disabled) {
        setIsDragActive(true);
      }
    },
    [disabled]
  );

  const handleDragLeave = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
  }, []);

  const handleDragOver = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragActive(false);

      if (disabled) return;

      const files = Array.from(e.dataTransfer.files);
      const imageFiles = files.filter((file) => file.type.startsWith('image/'));

      if (imageFiles.length > 0) {
        const remainingSlots = maxImages - uploadedImages.length;
        const filesToUpload = imageFiles.slice(0, remainingSlots);
        onImagesSelected(filesToUpload);
      }
    },
    [disabled, maxImages, uploadedImages.length, onImagesSelected]
  );

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (files && files.length > 0) {
        const imageFiles = Array.from(files).filter((file) =>
          file.type.startsWith('image/')
        );
        const remainingSlots = maxImages - uploadedImages.length;
        const filesToUpload = imageFiles.slice(0, remainingSlots);
        onImagesSelected(filesToUpload);
      }
      // Reset input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    [maxImages, uploadedImages.length, onImagesSelected]
  );

  const handleZoneClick = useCallback(() => {
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, [disabled]);

  const canUploadMore = uploadedImages.length < maxImages;

  return (
    <div className={styles.container}>
      {canUploadMore && (
        <div
          className={`${styles.uploadZone} ${isDragActive ? styles.dragActive : ''} ${disabled ? styles.disabled : ''}`}
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={handleZoneClick}
        >
          <div className={styles.uploadIcon}>📸</div>
          <div className={styles.uploadText}>
            Drag and drop images here, or click to select
          </div>
          <div className={styles.uploadHint}>
            Upload 50-70 screenshots (max {maxImages} images)
          </div>
          <input
            multiple
            ref={fileInputRef}
            type="file"
            className={styles.fileInput}
            accept="image/*"
            disabled={disabled}
            onChange={handleFileInputChange}
          />
        </div>
      )}

      {uploadedImages.length > 0 && (
        <>
          <div className={styles.thumbnailGrid}>
            {uploadedImages.map((image, index) => (
              <div key={index} className={styles.thumbnailItem}>
                <img
                  src={image.preview}
                  alt={`Upload ${index + 1}`}
                  className={styles.thumbnailImage}
                />
                {!image.isUploading && !image.error && (
                  <button
                    className={styles.removeButton}
                    aria-label="Remove image"
                    onClick={() => onRemoveImage(index)}
                  >
                    ×
                  </button>
                )}
                {image.isUploading && (
                  <div className={styles.progressOverlay}>
                    <div className={styles.progressBar}>
                      <div
                        className={styles.progressFill}
                        style={{ width: `${image.uploadProgress}%` }}
                      />
                    </div>
                    <div className={styles.progressText}>
                      {image.uploadProgress}%
                    </div>
                  </div>
                )}
                {image.error && (
                  <div className={styles.errorOverlay}>{image.error}</div>
                )}
              </div>
            ))}
          </div>
          <div className={styles.imageCount}>
            {uploadedImages.length} / {maxImages} images uploaded
          </div>
        </>
      )}
    </div>
  );
};

export default ImageUploadZone;
